// Importing React classes and functions from node modules
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getProfileUsers, getGroups, getAttendance, createAttendance, updateAttendance, getAllAttendance, updateFinalResults, createFinalResults, getFinalResultsByID } from "../data/repository";
import { format } from 'date-fns';

// Functional Component for Login Page
function Attendance(props) {
    
    // Declaration of useState Variables and Hook
    const [users, setUsersData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [groups, setGroupsData] = useState([]);
    const { groupNumber } = useParams();
    const [attendanceRecords, setAttendanceRecords] = useState([]);
    const [attendanceData, setAttendanceData] = useState([]);
    const [percentages, setPercentages] = useState({});
    const [genderFilter, setGenderFilter] = useState(props.user.gender === "Male" ? "Atfal" : props.user.gender === "Female" ? "Nasirat" : "All");
    const currentDate2 = format(new Date(), 'MM/dd/yyyy');

    useEffect(() => {

        // Loads User Details from DB
        async function loadUserDetails() {
            const currentDetails = await getProfileUsers();
            setUsersData(currentDetails)
            setIsLoading(false);
        }

        // Loads User Details from DB
        async function loadGroupDetails() {
            const currentGroups = await getGroups();
            setGroupsData(currentGroups)
        }

        const loadAttendanceData = async () => {
            const attendance = await getAttendance(currentDate2);
            setAttendanceData(attendance?.attendance || []);
        }

        const loadAllAttendanceData = async () => {
            const attendance = await getAllAttendance();
            const lastFiveRecords = attendance.slice(-8); // Get the last 8 records
            setAttendanceRecords(lastFiveRecords);
        }

        const initializeData = async () => {
            await loadUserDetails();
            await loadGroupDetails();
            await loadAttendanceData();
            await loadAllAttendanceData();
        }

        // Calls the functions above
        initializeData();

    }, [currentDate2]);

    useEffect(() => {
        const calculateAllPercentages = async () => {
            const percentages = {};
            const students = users.filter(user => 
                user.group !== "Vice Principal" &&
                user.group !== "Principal" &&
                user.group !== "Male Teacher" &&
                user.group !== "Female Teacher" &&
                user.group !== "Admin Staff" &&
                user.group !== "Admin"
            );
            for (const user of students) {
                const perc = await calculatePercentages(user.id);
                percentages[user.id] = perc;
                setPercentages(percentages); // Uncomment for slower db, Updates Percentages one by one 
            }
            // setPercentages(percentages); Uncomment for Faster DB, updates all at once, both work but to not confuse uers using above in slow db
        }

        if (users.length > 0) {
            calculateAllPercentages();
        }

    }, [users]);


    const handleSearch = async (event) => {
        setSearch(event.target.value.toLowerCase());
    }

    const findFatherName = (studentID) => {
        const userWithMatchingID = users.find(user => user.id === studentID);
        return userWithMatchingID ? userWithMatchingID.fathersName : "";
    };

    const findMotherName = (studentID) => {
        const userWithMatchingID = users.find(user => user.id === studentID);
        return userWithMatchingID ? userWithMatchingID.mothersName : "";
    };

    const getParentName = (studentID, gender) => {
        const fatherName = findFatherName(studentID);
        const motherName = findMotherName(studentID);
    
        if (gender === "Female" || gender === "Nasirat") {
            // Prefer mother's name, but fall back to father's name if mother's name is null/empty
            return motherName || fatherName || "";
        } else {
            // Prefer father's name, but fall back to mother's name if father's name is null/empty
            return fatherName || motherName || "";
        }
    };

    const handleAttendanceChange = async (studentId, status, studentGroup) => {

        let updatedAttendanceData = attendanceData.map((student) => {
            if (student && student.id === studentId) {
                return { ...student, status };
            }
            return student;
        });

        const existingAttendance = await getAttendance(currentDate2);;

        if (existingAttendance) {
            const existingStudent = updatedAttendanceData.find((student) => student?.id === studentId);
            if (!existingStudent) {
                updatedAttendanceData.push({
                    id: studentId,
                    group: studentGroup,
                    status: status,
                });
            }
        } else {
            updatedAttendanceData = [
                ...attendanceData,
                {
                    id: studentId,
                    group: studentGroup,
                    status: status,
                },
            ];
        }

        setAttendanceData(updatedAttendanceData);
        await syncAttendanceData(updatedAttendanceData);

        const percentages = await calculatePercentages(studentId);
        const presentMark = percentages.presentPercentage.toFixed(2);

        const existingFinalResults = await getFinalResultsByID(studentId);
        const student = users.find(user => user.id === studentId);

        if (existingFinalResults) {
            await updateFinalResults(studentId, presentMark);
        } else {
            await createFinalResults(studentId, student?.name, student?.group, student?.gender, student?.fathersName, student?.mothersName, student?.fathersEmail, student?.studentEmail, presentMark);
        }

        // Update the percentages state
        setPercentages(prevPercentages => ({
            ...prevPercentages,
            [studentId]: percentages
        }));
    };

    const syncAttendanceData = async (updatedAttendanceData) => {
        const existingAttendance = await getAttendance(currentDate2);

        if (existingAttendance) {
            await updateAttendance(currentDate2, updatedAttendanceData);
        } else {
            await createAttendance(currentDate2, updatedAttendanceData);
        }

        const attendance = await getAllAttendance();
        const lastFiveRecords = attendance.slice(-8); // Get the last 8 records
        setAttendanceRecords(lastFiveRecords);
    };

    // Calculate present and absent percentages
    const calculatePercentages = async (studentId) => {

        const newAttendanceData2 = await getAllAttendance();

        let totalAttendance = 0;
        let presentCount = 0;
        let absenceCount = 0;
        let approvedLeaveCount = 0;

        newAttendanceData2.forEach((record) => {
            record.attendance.forEach((student) => {
                if (student.id === studentId) {
                    totalAttendance++;
                    if (student.status === "Present") {
                        presentCount++;
                    } else if (student.status === "Absent") {
                        absenceCount++;
                    } else if (student.status === "Approved Leave") {
                        approvedLeaveCount++;
                    }
                }
            });
        });

        // Add 15% to the present count for approved leave
        presentCount += approvedLeaveCount * 0.15;

        return {
            presentPercentage: totalAttendance > 0 ? (presentCount / totalAttendance) * 100 : 0,
            absencePercentage: totalAttendance > 0 ? (absenceCount / totalAttendance) * 100 : 0,
        };
    };


    let groupDetails;

    const group = groups.find((group) => group.id === groupNumber);

    if (group) {
        groupDetails = group.group;
    } else {
        groupDetails = "Invalid group number";
    }


    return (

        <div>
            <p>&nbsp;</p>
            <h3 className="text-center">Mark Attendance:</h3>

            <br />
            <div className="searchCenter">
                <div className="form-group has-search">
                    <span className="fa fa-search form-control-feedback"></span>
                    <input type="text" style={{ border: "1px solid #112c3f", borderRadius: "10rem" }} className="form-control" placeholder="Search" aria-label="Search" onChange={handleSearch} />
                </div>
            </div>

            {(props.user.group === "Admin" || (props.user.group === "Principal" && props.user.gender === "Male")) && (
                <div style={{ textAlign: "center", margin: "0.5rem" }}>
                    <select style={{ border: "1px solid #112c3f", borderRadius: "0.5rem", padding: "0.25rem 0.5rem" }} value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)}>
                        <option value="All">All</option>
                        <option value="Atfal">Atfal</option>
                        <option value="Nasirat">Nasirat</option>
                    </select>
                    <div style={{ fontSize: "0.75rem", color: "#555", marginTop: "0.25rem" }}>
                        Gender
                    </div>
                </div>
            )}

            <div style={{ display: "flex", justifyContent: "center" }}>
                <Link to="/SelectGroupAttendance">
                    <button type="button" style={{ margin: "5px" }} className="text-center btn btn-success">Go Back to Select Group</button>
                </Link>
            </div>

            <div className="table-responsive">

                {isLoading ?
                    <div className="card-body text-center">
                        <span className="text-muted">Loading Students...</span>
                    </div>
                    :
                    <div>
                        <table className="table table-striped" style={{ margin: "0" }}>
                            <thead>
                                <tr>
                                    <th></th>
                                    <th style={{ color: "#112c3f" }} scope="col">ID</th>
                                    <th style={{ color: "#112c3f" }} scope="col">Name</th>
                                    <th style={{ color: "#112c3f" }} scope="col">Parent</th>
                                    <th style={{ color: "#112c3f" }} scope="col">Group</th>
                                    <th style={{ color: "#112c3f" }} scope="col">Gender</th>
                                    <th className="text-center" style={{ color: "#112c3f" }} scope="col">Mark Attendance</th>
                                    <th style={{ color: "#112c3f" }} scope="col">Last 8 Attendances</th>
                                    <th style={{ color: "#112c3f" }} scope="col">Total Absences</th>
                                    <th style={{ color: "#112c3f" }} scope="col">Total Presents</th>

                                </tr>
                            </thead>
                            {/* Mapping Users state Variable to access its content easily to display in Table */}
                            {users.filter((userDetails) => {
                                return search.toLowerCase() === '' ? userDetails : (userDetails.name && userDetails.name.toLowerCase().includes(search)) || (userDetails.group && userDetails.group.toLowerCase().includes(search)) || (userDetails.id && userDetails.id.toLowerCase().includes(search)) || (userDetails.gender && userDetails.gender.toLowerCase().includes(search));
                            }).map((userDetails) => {
                                // Check if the student ID exists in the attendanceData array
                                const attendanceStudent = attendanceData.find(
                                    (student) => student.id === userDetails.id

                                );

                                // Get the status if the student exists in the attendanceData array
                                const status = attendanceStudent ? attendanceStudent.status : "";

                                const lastFiveAttendances = attendanceRecords.map(record => record.attendance.find(student => student.id === userDetails.id));
                                const attendanceStatus = lastFiveAttendances
                                    .map((record) => {
                                        if (record) {
                                            if (record.status === "Present") {
                                                return "✅";
                                            } else if (record.status === "Absent") {
                                                return "❌";
                                            } else if (record.status === "Approved Leave") {
                                                return "🟠";
                                            }
                                        }
                                        return "🚫";
                                    })
                                    // .reverse()
                                    .join("");

                                // Get the percentages for the student
                                const studentPercentages = percentages[userDetails.id] || { presentPercentage: 0, absencePercentage: 0 };

                                return (
                                    <tbody key={userDetails.id}>
                                        {/* Dont display the name of the logged in user but the rest, And dont show Admin for teachers */}
                                        {(userDetails.id !== props.user.id && userDetails.group !== "Admin" && userDetails.group !== "Male Teacher" && userDetails.group !== "Female Teacher" && userDetails.group !== "Principal" && userDetails.group !== "Vice Principal" && userDetails.group !== "Admin Staff") &&
                                            <>
                                                {/* If logged in user is FemaleTeachers then Display only Nasirat List and If MaleTeahers are logged in show only Atfal list or if Admin is logged in show full list*/}
                                                {((props.user.group === "Female Teacher" && userDetails.gender === "Nasirat" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    (props.user.group === "Male Teacher" && userDetails.gender === "Atfal" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    (props.user.group === "Admin" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    // (props.user.group === "Admin" && props.user.id === "MAAdminAcc" && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    (props.user.group === "Principal" && props.user.gender === "Female" && userDetails.gender === "Nasirat" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    // (props.user.group === "Principal" && props.user.gender === "Male" && userDetails.gender === "Atfal" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    (props.user.group === "Principal" && props.user.gender === "Male" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    (props.user.group === "Vice Principal" && props.user.gender === "Female" && userDetails.gender === "Nasirat" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    (props.user.group === "Vice Principal" && props.user.gender === "Male" && userDetails.gender === "Atfal" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    (props.user.group === "Admin Staff" && props.user.gender === "Female" && userDetails.gender === "Nasirat" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails)) ||
                                                    (props.user.group === "Admin Staff" && props.user.gender === "Male" && userDetails.gender === "Atfal" && userDetails.archived !== true && (groupNumber === "5" || userDetails.group === groupDetails))
                                                ) &&
                                                    ((props.user.group === "Admin" || (props.user.group === "Principal" && props.user.gender === "Male"))
                                                    ? (genderFilter === "All" || userDetails.gender?.toLowerCase() === genderFilter.toLowerCase()) : true) &&
                                                    <tr>
                                                        <td></td>
                                                        <td style={{ color: "#112c3f" }}>{userDetails.id}</td>
                                                        <td style={{ color: "#112c3f" }}>{userDetails.name}</td>
                                                        <td style={{ color: "#112c3f" }}>
                                                        {getParentName(userDetails.id, userDetails.gender)}
                                                        </td>
                                                        <td style={{ color: "#112c3f" }}>{userDetails.group}</td>
                                                        <td style={{ color: "#112c3f" }}>{userDetails.gender}</td>
                                                        <td style={{ color: "#112c3f" }}>
                                                            <div className="attendance-buttons">
                                                                <button className={`attendance-button ${status === "Present" ? "selected present" : "present"}`} onClick={() => handleAttendanceChange(userDetails.id, "Present", userDetails.group)} onTouchStart={() => handleAttendanceChange(userDetails.id, "Present", userDetails.group)}>Present</button>
                                                                <button className={`attendance-button ${status === "Absent" ? "selected absent" : "absent"}`} onClick={() => handleAttendanceChange(userDetails.id, "Absent", userDetails.group)} onTouchStart={() => handleAttendanceChange(userDetails.id, "Absent", userDetails.group)}>Absent</button>
                                                                <button className={`attendance-button ${status === "Approved Leave" ? "selected approved" : "approved"}`} onClick={() => handleAttendanceChange(userDetails.id, "Approved Leave", userDetails.group)} onTouchStart={() => handleAttendanceChange(userDetails.id, "Approved Leave", userDetails.group)}>Approved Leave</button>
                                                            </div>
                                                            {/* Either Use Buttons for Marking Attendance or Use Below Drop Down to mark Attendance */}
                                                            {/* <select
                                                                className={`form-control ${status === "Present" ? "text-success" : status === "Absent" ? "text-danger" : status === "Approved Leave" ? "text-warning" : ""}`}
                                                                value={status}
                                                                onChange={(event) => handleAttendanceChange(userDetails.id, event.target.value, userDetails.group)}
                                                                style={{
                                                                    width: "100px",
                                                                    display: "inline-block",
                                                                    backgroundColor: "#fff",
                                                                    border: `2px solid ${status === "Present" ? "green" : status === "Absent" ? "red" : status === "Approved Leave" ? "orange" : "gray"}`,
                                                                    borderRadius: "4px"
                                                                }}
                                                            >
                                                                <option value="Present">Present</option>
                                                                <option value="Absent">Absent</option>
                                                                <option value="Approved Leave">Approved Leave</option>
                                                            </select> */}
                                                        </td>
                                                        {/* <td style={{ color: "#112c3f" }}>➡{attendanceStatus}</td> */}
                                                        <td style={{ color: "#112c3f", verticalAlign: "top" }}>
                                                            <div style={{ display: "flex", flexDirection: "column" }}>
                                                                <span>➡</span>
                                                                <span>{attendanceStatus}</span>
                                                            </div>
                                                        </td>
                                                        <td style={{ color: "#112c3f" }}>{studentPercentages.absencePercentage.toFixed(2)}%</td>
                                                        <td style={{ color: "#112c3f" }}>{studentPercentages.presentPercentage.toFixed(2)}%</td>

                                                    </tr>
                                                }
                                            </>
                                        }


                                    </tbody>
                                )
                            })}
                        </table>
                    </div>
                }
            </div>
        </div>
    );
}

// Export the Attendance Function
export default Attendance;
